import { ReactComponent as AddIcon } from 'assets/icons/add24.svg'
import { message as antdMessage } from 'antd'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useProperties } from 'hooks/queries/companies/properties/useProperties'
import { useWorkflow } from 'hooks/queries/workflow/useWorkflow'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryParams } from 'hooks/useQueryParams'
import { isEqual } from 'lodash'
import { observer } from 'mobx-react-lite'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { PersistedPropertyValue, Reassignment, IWorkflow, StatePropertyValue } from 'stores/Workflows/Types'
import { Property } from 'api/clients/PropertyClient'
import { useHistory, useLocation } from 'react-router-dom'
import { Workflow } from 'stores/Workflows/Workflow'
import { reportError } from 'utils/error'
import { usePollUpdateWorkflow } from 'hooks/queries/workflow/usePollUpdateWorkflow'
import { useKanbanView } from 'screens/Finder/components/views/KanbanView/hooks/useKanbanView'
import { ErrorState } from '../ErrorState'
import { getStatesToReassign } from './getStatesToReassign'
import { LoadingState } from '../LoadingState'
import { WorkflowBuilderHandle, UpdateFormProps } from './Types'
import { useConfirmUnsavedChanges } from '../useConfirmUnsavedChanges'
import { useReassignmentsModal } from './useReassignmentsModal'
import { templates, TemplateType } from 'components/FolderTemplatesDialog/Templates'
import { workflowFromTemplate } from '../utils'
import Styles from './UpdateForm.module.scss'
import { List } from '../List'
import { Intro } from '../Intro'
import { Modal } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const UpdateForm = observer(
  forwardRef<WorkflowBuilderHandle, UpdateFormProps>(({ folderId, onDelete, onClose }, fRef) => {
    const [workflowSnapshot, setWorkflowSnapshot] = useState<Workflow>()
    const [workflow, setWorkflow] = useState<Workflow>()
    const [errors, setErrors] = useState<string[]>([])
    const reassignmentsPrompt = useReassignmentsModal()
    const confirmUnsavedChanges = useConfirmUnsavedChanges()
    const properties = useProperties({}, { enabled: false }, { pushToStore: true })
    const currentWorkspaceId = useCurrentWorkspaceId()
    const kanban = useKanbanView()
    const { templateId } = useQueryParams()
    const history = useHistory()
    const location = useLocation()
    const { isMobile } = useResponsiveDesign()

    const { data, isLoading, refetch } = useWorkflow({ folderId })
    const { mutateWithPolling, isLoading: isSaving } = usePollUpdateWorkflow({ folderId })
    const isDirty = Boolean(workflow && workflowSnapshot && !isEqual(workflow.toJSON(), workflowSnapshot.toJSON()))

    useImperativeHandle(
      fRef,
      () => ({
        confirmUnsavedChanges: async () => {
          if (!isDirty) {
            return true
          }
          return confirmUnsavedChanges()
        },
        isDirty: () => isDirty,
      }),
      [isDirty, confirmUnsavedChanges],
    )

    useEffect(() => {
      if (data) {
        let workflowData = data.workflow as IWorkflow<Property<StatePropertyValue>>
        if (templateId) {
          const template = templates.find((t) => t.id === templateId)

          if (template && template.id !== TemplateType.Custom) {
            workflowData = workflowFromTemplate({ template, workflowData })
          }
        }

        setWorkflow(Workflow.fromJSON(workflowData))
        setWorkflowSnapshot(Workflow.fromJSON(data.workflow))
      }
    }, [data, templateId])

    // Reset errors when resetting the form
    useEffect(() => {
      if (!isDirty) {
        setErrors([])
      }
    }, [isDirty])

    if (!data && isLoading) {
      return <LoadingState />
    }

    if (!data || !workflow || !workflowSnapshot) {
      return <ErrorState />
    }

    const save = async () => {
      setErrors([])
      const errors = workflow.validate()
      if (errors.length > 0) {
        showErrorMessageToast(errors[0])
        return
      }

      let reassignments: Reassignment[] = []
      try {
        const statesToReassign = await getStatesToReassign(workflowSnapshot, workflow, currentWorkspaceId, folderId)

        if (statesToReassign.length > 0) {
          const reassignmentsMap = await reassignmentsPrompt({ statesToReassign, newStates: workflow.states })
          if (!reassignmentsMap) {
            return
          }
          reassignments = Array.from(reassignmentsMap, ([fromState, toState]) => {
            return {
              from_id: (fromState.propertyValue as PersistedPropertyValue).id,
              to_id: (toState.propertyValue as PersistedPropertyValue).id,
            }
          })
        }
      } catch (e) {
        reportError(e)
        showToastMessage('An error occurred, our team has been notified.', 'error')
        return
      }

      const { configuration, property_values } = workflow.toJSON()
      showToastMessage('Saving changes...', 'loading')
      mutateWithPolling(
        {
          property_id: workflow.propertyId,
          property_values: property_values,
          config: configuration,
          reassignments,
        },
        {
          onSuccess: () => {
            antdMessage.destroy()
            showToastMessage('Changes saved', 'success')
            refetch()

            // Reload kanban so that any changes added to the statuses (columns) is reflected
            // in the underlying view.
            kanban.reload()
            // Refresh the loaded properties so that if a new status was added to the workflow
            // it doesn't appear as disabled in the kanban view.
            properties.refetch()

            if (templateId) {
              const queryParams = new URLSearchParams(location.search)
              queryParams.delete('templateId')
              history.replace({ search: queryParams.toString() })
            }
          },
          onError(error) {
            antdMessage.destroy()
            reportError(error)
            showToastMessage('An error occurred, our team has been notified.', 'error')
          },
        },
      )
    }

    const createState = () => {
      workflow.createState({}).setFocus(true)
    }

    return (
      <>
        {isMobile && <Modal.Header title="Statuses and Automation" onClose={onClose} />}
        <Modal.Content>
          <Intro />
          <List workflow={workflow} />
          {errors.length > 0 && <p className={Styles.errors}>{errors.join(',')}</p>}

          <Modal.FooterButton frontIcon={AddIcon} variant="secondary" size="lg" onPress={createState}>
            New Status
          </Modal.FooterButton>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            {onDelete && (
              <Modal.FooterButton
                variant="secondary"
                size="lg"
                onPress={() => onDelete(data.workflow)}
                isDisabled={isSaving}
              >
                Delete Workflow
              </Modal.FooterButton>
            )}
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Modal.FooterButton size="lg" onPress={save} isDisabled={isSaving || !isDirty}>
              Save Changes
            </Modal.FooterButton>
          </Modal.FooterContent>
        </Modal.Footer>
      </>
    )
  }),
)

export { UpdateForm }
